import { cn } from '@syncanix/ui';
import { NavLink } from 'react-router-dom';

import type { ReactElement } from 'react';

/**
 * One sidebar nav row — the item shape, its count pill, and its overflow
 * tooltip.
 *
 * Split out of `Sidebar.tsx`, which owns the rail: its widths, the group
 * partitioning, and the `<nav>` landmark. The row grew past what left the shell
 * under the ~300-line ceiling, and the seam is a real one — everything here is
 * about a single item, nothing here knows the rail exists.
 */

export interface SidebarItem {
  to: string;
  label: string;
  /** Optional icon — a 16-20px SVG that inherits currentColor. */
  icon?: ReactElement;
  /** Disabled items render but do not navigate; used for "coming in Pass N". */
  disabled?: boolean;
  /**
   * Optional group label (e.g. 'AGENT' | 'SURFACES' | 'PLATFORM'). Items
   * with the same `group` value render under a single labelled region.
   * Items without `group` render flat (legacy behaviour). Mixed mode is
   * supported: ungrouped items render first, then groups in
   * first-occurrence order.
   */
  group?: string;
  /**
   * Optional accent-soft count pill on the row (SYN-119). Wire ONLY real,
   * cheaply-available counts — never a fabricated/zero placeholder. The pill is
   * hidden when `count` is undefined or `0`; on the EXPANDED rail it renders the
   * count, on the COLLAPSED rail it degrades to a minimal dot on the icon (no
   * room for a pill beside an icon-only row). `countAriaLabel` is the localized
   * screen-reader phrase (e.g. "3 pending"); when omitted the bare number is
   * announced.
   */
  count?: number;
  countAriaLabel?: string;
  /**
   * Optional pre-formatted, numbering-system-aware visible pill text (with any
   * localized overflow, e.g. "99+"). Supply it from a caller that has the active
   * locale so the digits honour the workspace numbering system. When omitted the
   * component falls back to the raw-Latin {@link formatCount} of `count`.
   */
  countLabel?: string;
}

/**
 * Count above which the pill collapses to an overflow indicator (`99+`), so a
 * large queue can't widen the row. Exported so a caller building a
 * numbering-system-aware `countLabel` uses the same threshold as the fallback.
 */
export const COUNT_OVERFLOW = 99;

/**
 * Fallback visible pill text (raw Latin digits), capped at `99+`. Used only when
 * the caller supplies no pre-formatted `countLabel` — see {@link SidebarItem}.
 */
function formatCount(count: number): string {
  return count > COUNT_OVERFLOW ? `${COUNT_OVERFLOW}+` : String(count);
}

/**
 * Put a native tooltip on an EXPANDED label only when it is genuinely cut off.
 *
 * `truncate` on the expanded rail is a silent ellipsis: measured in Chromium at
 * the real row box (252px rail − 24 nav gutter − 16 row gutter − 16 icon − 8 gap
 * = a 188px label box, Geist 14px), the Spanish `sidebar.conversationLogs`
 * ("Registros de conversaciones") renders 199.6px and gets clipped with no way
 * for a sighted pointer user to read it. Every locale's labels are a translation
 * away from that, so the fix is the affordance, not a shorter string.
 *
 * Deliberately on the SPAN, not the link, and deliberately conditional: an
 * expanded row whose label is fully visible still carries no tooltip, because a
 * native tooltip duplicating on-screen text is noise. The accessible name is
 * unaffected either way — the full label is always the link's text.
 *
 * A ref callback rather than a hook, because rows render inside a `.map()`.
 * It re-runs on re-render, which costs one `scrollWidth` read per row per
 * sidebar render (a route change, not a frame) and buys a re-measure after the
 * webfont swaps in — the first measurement happens against the fallback face.
 */
function applyOverflowTitle(node: HTMLElement | null, label: string): void {
  if (!node) return;
  // +1px: sub-pixel text metrics make scrollWidth exceed clientWidth by a
  // fraction on labels that are not actually clipped.
  if (node.scrollWidth > node.clientWidth + 1) node.setAttribute('title', label);
  else node.removeAttribute('title');
}

/**
 * Render one row. A function rather than a component so the rail can call it
 * straight from its `.map()` without a wrapper element between the `<ul>` and
 * the `<li>` it returns.
 */
export function renderSidebarItem(item: SidebarItem, collapsed: boolean): ReactElement {
  return (
    <li key={item.to}>
      {item.disabled ? (
        <span
          aria-disabled="true"
          className={cn(
            'flex h-navitem items-center gap-2.5 rounded-sm px-2.5 text-[13.5px] text-tertiary/70',
            collapsed && 'justify-center',
          )}
          // Native tooltip only on the collapsed rail, where the label is
          // sr-only; expanded items show the label inline, so a `title` there
          // is a redundant duplicate tooltip.
          title={collapsed ? item.label : undefined}
        >
          {item.icon ? (
            <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">
              {item.icon}
            </span>
          ) : null}
          {collapsed ? (
            <span className="sr-only">{item.label}</span>
          ) : (
            <span
              className="truncate"
              ref={(node) => {
                applyOverflowTitle(node, item.label);
              }}
            >
              {item.label}
            </span>
          )}
        </span>
      ) : (
        <NavLink
          to={item.to}
          // Collapsed-rail tooltip only: the label is sr-only when collapsed,
          // visible inline when expanded — so an expanded `title` would be a
          // redundant native tooltip duplicating the on-screen label.
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            cn(
              // Bedrock rail row: dense 34px, the `controls` radius, 13.5px at
              // the 520 control weight. Idle nav is the secondary ink and
              // brightens to full ink on hover/focus — an idle rail rendered at
              // full ink competes with the page, and then nothing in it reads
              // as selected. Active rows carry the accent-soft wash plus the
              // gradient indicator bar.
              'relative flex h-navitem items-center gap-2.5 rounded-sm px-2.5 text-[13.5px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              isActive
                ? 'bg-primary-subtle font-control text-foreground'
                : 'text-muted-foreground hover:bg-surface-well hover:text-foreground focus-visible:bg-surface-well focus-visible:text-foreground',
              collapsed && 'justify-center',
            )
          }
        >
          {({ isActive }) => (
            <>
              {/* Active indicator: the brand gradient pinned to the inline-start
                  edge (RTL flips it to the inline-end automatically). */}
              {isActive ? (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-1.5 start-0 w-0.5 rounded-full bg-brand-gradient"
                />
              ) : null}
              {item.icon ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative flex h-4 w-4 shrink-0 items-center justify-center',
                    isActive && 'text-primary-strong',
                  )}
                >
                  {item.icon}
                  {/* Collapsed rail has no room for the count pill, so the
                      "there's something here" signal degrades to a minimal dot
                      pinned to the icon's inline-end/top corner (RTL flips via
                      the logical `end-0`). The exact count still reaches screen
                      readers through the sr-only label below. */}
                  {collapsed && item.count !== undefined && item.count > 0 ? (
                    <span className="absolute end-0 top-0 h-1.5 w-1.5 rounded-full bg-primary ring-1 ring-surface" />
                  ) : null}
                </span>
              ) : null}
              {collapsed ? (
                <span className="sr-only">
                  {item.count !== undefined && item.count > 0
                    ? `${item.label} (${item.countAriaLabel ?? String(item.count)})`
                    : item.label}
                </span>
              ) : (
                <span
                  className="min-w-0 flex-1 truncate"
                  ref={(node) => {
                    applyOverflowTitle(node, item.label);
                  }}
                >
                  {item.label}
                </span>
              )}
              {/* Count pill (SYN-119): real counts only, hidden when collapsed /
                  undefined / zero. A SOLID accent fill (not the accent-soft wash)
                  so the badge shape stays distinct even when its own row is the
                  active route — whose background IS the accent-soft wash. The
                  `tabular-nums` keeps the digits from shifting the row width. */}
              {!collapsed && item.count !== undefined && item.count > 0 ? (
                <span className="ms-auto inline-flex min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 py-px text-[10.5px] font-semibold tabular-nums text-primary-foreground">
                  <span aria-hidden="true">{item.countLabel ?? formatCount(item.count)}</span>
                  <span className="sr-only">{item.countAriaLabel ?? String(item.count)}</span>
                </span>
              ) : null}
            </>
          )}
        </NavLink>
      )}
    </li>
  );
}
